<template>
  <div v-if="showComponent" :class="{ row : isOnModeLabelOnLeft}">
    <!-- <hr class="mt-0"> -->
    <label class="mb-4 d-flex justify-content-between">
      <h4 class="d-inline my-0">{{label}}</h4>
      <div>
        <font-awesome-icon
          id="font-awesome-icon-edit"
          icon="edit"
          size="sm"
          class="clickable float-right mt-1"
          :class="{ 'ml-2': help }"
          v-if="showEditButton"
          @click="$emit('edit-button-clicked')"
        />
        <font-awesome-icon
          icon="question-circle"
          size="sm"
          class="clickable float-right mt-1"
          v-if="help"
          v-b-tooltip.hover="help"
        />
        <lm-dropdown
          v-if="isEditableByPickedUser"
          class="float-right"
          :tooltip="!disableEditors ? 'Selecciona quién edita este campo' : null"
          template="user-list"
          @input="$emit('editor-change', $event)"
          :value="collaborator"
          :disabled="disableEditors"
          closeAfterPick
        >
          <div class="d-flex align-items-center gap-2">
            {{ collaborator.name || '' }}
            <a-icon type="edit" v-if="!disableEditors" />
          </div>
        </lm-dropdown>
      </div>
    </label>
    <LmLoading v-if="loading" />
    <div v-else>
      <div class="row" v-for="(data, gIndex) of groups" :key="gIndex">
        <div class="col-12 d-inline-flex justify-content-between align-items-center">
          <h6>{{`${labelGroup || 'Grupo'} ${isLabelGroupNumber ? gIndex + 1 : ''}`}}</h6>
          <font-awesome-icon
            v-if="groups.length > 1 && !disabled && documentAllowToRemoveGroup"
            :key="gIndex"
            id="font-awesome-icon-edit"
            icon="times"
            size="sm"
            class="clickable float-right mt-1"
            @click="onRemoveGroup(gIndex)"
            v-b-tooltip.hover="'Eliminar Grupo'"
          />
        </div>
        <hr>
        <template v-for="(item, eIndex) in items.formElements">
          <component
            :disabled="disabled || isDisabledByEditor(item, gIndex) || isDisabledInDocumentCtx(item, gIndex)"
            :ref="`componentRef${eIndex}_${gIndex}`"
            :key="`label_${eIndex}_${gIndex}`"
            :id="`componentId${eIndex}_${gIndex}`"
            :class="item.class"
            :is="item.componentName"
            :v="$v.values[`group_${gIndex}`][item.formObjectToSendToServerProperty]"
            v-bind="item.componentProps"
            v-model="data[item.formObjectToSendToServerProperty]"
            :editor="formRowEditor(item, gIndex)"
            @input="onInput"
            @onUpload="onUpload"
            @editor-change="onEditorChange(item, gIndex, $event)"
            @regenerate-validations="onRegenerateValidations"
          />
        </template>
      </div>
    </div>
    <LmButton
      v-if="!disabled && showAddButton"
      :disabled="!items.formElements.length"
      lmButtonRootWrapperClass="d-inline-block"
      buttonClass="btn-primary mt-2 mb-2"
      :value="$t(buttonText)"
      type='button'
      @click="onAddGroup"
    />
    <hr class="mb-4">
  </div>
</template>

<script>
import {
  required,
  email,
  minLength,
  maxLength,
  numeric,
  sameAs
} from 'vuelidate/lib/validators'
import _ from 'lodash'
import lmInputMixin from '@/mixins/lmInputMixin.js'
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmPassword from '@/components/form/elements/LmPassword'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmNumberInput from '@/components/form/elements/LmNumberInput'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2'
import LmDate from '@/components/form/elements/LmDate'
import LmDateTime from '@/components/form/elements/LmDateTime'
import LmTextArea from '@/components/form/elements/LmTextArea'
import LmSelect from '@/components/form/elements/LmSelect'
import LmFormSubtitles from '@/components/form/elements/LmFormSubtitles'
import LmFormEmptySpace from '@/components/form/elements/LmFormEmptySpace'
import LmFormSeparator from '@/components/form/elements/LmFormSeparator'
import LmEditor from '@/components/form/elements/lmEditor'
import LmLeaFletMap from '@/components/LmLeaFletMap.vue'
import LmHtmlContent from '@/components/form/elements/LmHtmlContent'
import LmColorPicker from '@/components/form/elements/LmColorPicker'
import LmCheckbox from '@/components/form/elements/LmCheckbox'
import LmRadio from '@/components/form/elements/LmRadio'
import LmUploadFiles from '@/components/form/elements/LmUploadFiles'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmTagsInput from '@/components/form/elements/LmTagsInput'
import LmCodeInput from '@/components/form/elements/LmCodeInput'
import LmReaderInput from '@/components/form/elements/LmReaderInput'

export default {
  inject: {
    document: {
      type: Object,
      default: null
    }
  },
  mixins: [lmInputMixin],
  components: {
    LmButton,
    LmTextInput,
    LmPassword,
    LmEmailInput,
    LmNumberInput,
    LmNumberInputV2,
    LmTextArea,
    LmSelect,
    LmFormSubtitles,
    LmFormEmptySpace,
    LmFormSeparator,
    LmDate,
    LmDateTime,
    LmEditor,
    LmLeaFletMap,
    LmHtmlContent,
    LmColorPicker,
    LmCheckbox,
    LmRadio,
    LmUploadFiles,
    LmTreeSelect,
    LmTagsInput,
    LmCodeInput,
    LmReaderInput
  },
  data () {
    return {
      firstLoad: true,
      items: {
        formElements: []
      },
      validations: {},
      loading: false,
      values: {},
      groups: []
    }
  },

  validations () {
    const obj = { values: {} }
    for (const key in this.values) {
      for (const item of this.items.formElements) {
        if ('validations' in item) {
          for (const property in item.validations) {
            // Me aseguro de que no se pisen las validaciones y poder efectuar mas de una
            switch (property) {
              case 'required':
                _.set(
                  obj.values,
                  `[${key}][${item.formObjectToSendToServerProperty}][${property}]`,
                  required
                )
                break
              case 'email':
                _.set(
                  obj.values,
                  `[${key}][${item.formObjectToSendToServerProperty}][${property}]`,
                  email
                )
                break
              case 'minLength':
                _.set(
                  obj.values,
                  `[${key}][${item.formObjectToSendToServerProperty}][${property}]`,
                  minLength(Number(item.validations[property]))
                )
                break
              case 'maxLength':
                _.set(
                  obj.values,
                  `[${key}][${item.formObjectToSendToServerProperty}][${property}]`,
                  maxLength(Number(item.validations[property]))
                )
                break
              case 'numeric':
                _.set(
                  obj.values,
                  `[${key}][${item.formObjectToSendToServerProperty}][${property}]`,
                  numeric
                )
                break
              case 'sameAs':
                _.set(
                  obj.values,
                  `[${key}][${item.formObjectToSendToServerProperty}][${property}]`,
                  sameAs(item.validations[property])
                )
                break
              default:
                break
            }
          }
        } else {
          _.set(
            obj.values,
            `[${key}][${item.formObjectToSendToServerProperty}]`,
            ''
          )
        }
      }
    }
    return obj
  },
  props: {
    isVisible: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: ''
    },
    defaultValue: {
      type: String,
      default: ''
    },
    props: {
      type: Object,
      default: () => ({})
    },
    initialItems: {
      type: Object,
      default: () => ({})
    },
    buttonText: {
      type: String,
      default: ''
    },
    labelGroup: {
      type: String,
      default: 'Grupo'
    },
    isLabelGroupNumber: {
      type: Boolean,
      default: true
    },
    isOnPreviewMode: {
      type: Object,
      default: () => ({})
    },
    v: {
      type: Object,
      default: () => ({})
    },
    editors: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    showAddButton () {
      if (this.document?.isStarted) {
        return (
          this.editor.id === this.$store.getters['session/userInfo'].id &&
          this.isEditableByPickedUser
        )
      }
      return true
    },
    documentAllowToRemoveGroup () {
      if (this.document?.isStarted) {
        return (
          this.editor.id === this.$store.getters['session/userInfo'].id &&
          this.isEditableByPickedUser
        )
      }
      return true
    }
  },
  watch: {
    initialItems: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val?.formElements?.length) {
          this.items.formElements = val.formElements
        }
      }
    },
    value: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && !this.isOnPreviewMode) {
          const serialized = []
          Object.values(val).forEach((groupData) => {
            serialized.push(groupData)
          })
          if (!_.isEqual(this.groups, serialized)) {
            this.groups = serialized
            // NOTA. ahora no hay huecos en los grupos y serializan para
            // que sigan un orden ascendiente.
            // Este método se usa acá para que no haya errores con los
            // grupos con huecos. Ej: group_0, group_2, etc
            this.onInput()
          }
          this.values = val
        } else {
          if (this.firstLoad) {
            this.onAddGroup()
            this.firstLoad = false
          }
        }
      }
    }
  },
  methods: {
    onRegenerateValidations () {
      // const validations = _.cloneDeep(this.originalValidations)
      // if (validations) {
      //   for (const ref in this.$refs) {
      //     if (/^componentRef\d+/.test(ref)) {
      //       const component = this.$refs[ref][0]
      //       if (component?.modelName && !component?.showComponent) {
      //         delete validations[component.modelName]
      //       }
      //     }
      //   }
      // }
      // this.validationsData = validations
    },
    onEditorChange (formRow, groupIndex, event) {
      // if (this.validationsData) {
      //   const currentUser = this.$store.getters['session/userInfo']
      //   const validations = _.cloneDeep(this.validationsData)
      //   if (event.id !== currentUser.id) {
      //     delete validations[formRow.formObjectToSendToServerProperty]
      //   } else {
      //     validations[formRow.formObjectToSendToServerProperty] = this.originalValidations[formRow.formObjectToSendToServerProperty]
      //   }
      //   this.validationsData = validations
      // }
      this.$emit('editor-change-on-subelement', {
        editor: event,
        subelement: `${groupIndex}.${formRow.formObjectToSendToServerProperty}`
      })
    },
    formRowEditor (formRow, group) {
      return this.editors.find(el => el.subelement === `${group}.${formRow.formObjectToSendToServerProperty}`) || {}
    },
    isDisabledByEditor (formRow, group) {
      const editor = this.formRowEditor(formRow, group)
      if (Object.keys(editor).length) {
        return this.$store.getters['session/userInfo'].id !== editor.id
      }
      return false
    },
    isDisabledInDocumentCtx (formRow, group) {
      if (this.document === null) return false

      if (this.document?.isStarted) {
        const editor = this.formRowEditor(formRow, group)
        if (!Object.keys(editor).length && !this.isEditableByPickedUser) return true
      }
      return false
    },
    // Elimino el grupo de items y emito los cambios
    onRemoveGroup (groupIndex) {
      this.groups.splice(groupIndex, 1)
      this.onInput()
      this.$v.$reset()
    },
    onAddGroup () {
      const nextGroup = this.groups.length + 1

      const values = {}
      for (const element of this.items.formElements) {
        // Esto es vital para poder setear el valor por defecto para el campo
        // LmReaderInput o editor con variables
        if (element?.componentProps?.defaultValue && !this.isOnEditMode) {
          values[element.formObjectToSendToServerProperty] = element.componentProps.defaultValue
        } else {
          values[element.formObjectToSendToServerProperty] = ''
        }

        if (element.componentProps.isEditableByPickedUser) {
          const currentUser = this.$store.getters['session/userInfo']
          const event = {
            id: currentUser.id,
            name: currentUser.firstname + ' ' + currentUser.lastname
          }
          this.onEditorChange(element, nextGroup, event)
        }
      }

      this.groups.push(values)
      this.onInput()
    },
    onUpload (event) {
      // El componente LmUploadFiles no emite @change.native
      this.$emit('input', this.formObjectToSendToServer)
    },
    onInput () {
      const inputData = {}

      for (const [index, values] of this.groups.entries()) {
        inputData[`group_${index}`] = values
      }
      this.values = inputData
      this.$emit('input', inputData)
    }
  }
}
</script>
